import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Navbar } from '../Navbar';
import { Footer } from '../Footer';
import { DollarSignIcon, CreditCardIcon, FileTextIcon, DownloadIcon, PlusIcon, XIcon, AlertCircleIcon, CalendarIcon, CheckCircleIcon, InfoIcon, LoaderIcon } from 'lucide-react';
import { useTheme } from '../providers/ThemeProvider';
import { showToast } from '../ToastContainer';
import { openPaddleCheckout } from '../../services/paddleService';
interface PaymentMethod {
  id: string;
  type: 'card';
  name: string;
  cardNumber: string;
  last4: string;
  expiryMonth: string;
  expiryYear: string;
  cvc?: string;
  zip?: string;
  isDefault: boolean;
  brand: string;
}
// Helper function to detect card brand from number
const detectCardBrand = (cardNumber: string): string => {
  const cleaned = cardNumber.replace(/\s/g, '');
  if (/^4/.test(cleaned)) return 'visa';
  if (/^5[1-5]/.test(cleaned)) return 'mastercard';
  if (/^3[47]/.test(cleaned)) return 'amex';
  if (/^6(?:011|5)/.test(cleaned)) return 'discover';
  return 'card';
};
export const BillingPayoutsPage = () => {
  const {
    currentUser,
    userData
  } = useAuth();
  const {
    theme
  } = useTheme();
  const darkMode = theme === 'dark';
  // Payment methods state - START WITH EMPTY ARRAY (no fake data)
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  // Subscription state — price is dynamically loaded from Firestore
  const firestorePrice = userData?.subscription?.plan?.price ?? 0;
  const [subscription, setSubscription] = useState({
    plan: 'Teacher Monthly Plan',
    interval: 'Monthly',
    price: firestorePrice,
    nextBillingDate: 'December 15, 2024',
    status: 'active' // 'active' | 'trial' | 'canceled'
  });
  // Sync price from Firestore when userData loads/changes
  useEffect(() => {
    const price = userData?.subscription?.plan?.price;
    if (price != null) {
      setSubscription(prev => ({
        ...prev,
        price
      }));
    }
  }, [userData?.subscription?.plan?.price]);
  // Modal states
  const [isManageSubOpen, setIsManageSubOpen] = useState(false);
  const [isConfirmCancelOpen, setIsConfirmCancelOpen] = useState(false);
  const [isCardModalOpen, setIsCardModalOpen] = useState(false);
  const [cardModalMode, setCardModalMode] = useState<'add' | 'edit'>('add');
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null);
  const [isRemoveCardModalOpen, setIsRemoveCardModalOpen] = useState(false);
  // Loading states
  const [isProcessing, setIsProcessing] = useState(false);
  // Form states
  const [formCard, setFormCard] = useState<Omit<PaymentMethod, 'id' | 'isDefault' | 'type' | 'last4' | 'brand'>>({
    name: '',
    cardNumber: '',
    expiryMonth: '',
    expiryYear: '',
    cvc: '',
    zip: ''
  });
  // Form errors
  const [cardErrors, setCardErrors] = useState({
    name: '',
    cardNumber: '',
    expiry: '',
    cvc: '',
    zip: ''
  });
  // Loading state for Paddle portal redirect
  const [isPortalLoading, setIsPortalLoading] = useState(false);
  // Handle "Manage Subscription" — redirect to Paddle Customer Portal
  const handleOpenManageSub = async () => {
    const paddleCustomerId = userData?.subscription?.paddleCustomerId;
    if (!paddleCustomerId) {
      // No Paddle customer yet (still on trial or no subscription) — fall back to local modal
      setIsManageSubOpen(true);
      return;
    }
    setIsPortalLoading(true);
    try {
      // Get Firebase auth token
      const idToken = await currentUser?.getIdToken();
      if (!idToken) {
        throw new Error('Not authenticated');
      }
      const response = await fetch('https://us-central1-maestro-connect-2e3f0.cloudfunctions.net/createCustomerPortal', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`
        },
        body: JSON.stringify({
          customerId: paddleCustomerId
        })
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to open billing portal');
      }
      const {
        url
      } = await response.json();
      if (url) {
        window.location.href = url;
      } else {
        throw new Error('No portal URL returned');
      }
    } catch (error: any) {
      console.error('[Billing] Failed to open customer portal:', error);
      showToast.error('Unable to open billing portal. Please try again.');
      setIsPortalLoading(false);
    }
  };
  const handleOpenConfirmCancel = () => {
    setIsManageSubOpen(false);
    setIsConfirmCancelOpen(true);
  };
  const handleConfirmCancel = () => {
    setIsProcessing(true);
    setTimeout(() => {
      setSubscription({
        ...subscription,
        status: 'canceled'
      });
      setIsConfirmCancelOpen(false);
      setIsProcessing(false);
      showToast.success(`Subscription canceled. You'll keep access until ${subscription.nextBillingDate}.`);
    }, 600);
  };
  // Handle payment method modal with auto-fill
  const handleOpenCardModal = (mode: 'add' | 'edit', cardId?: string) => {
    setCardModalMode(mode);
    if (mode === 'edit' && cardId) {
      setSelectedCardId(cardId);
      const card = paymentMethods.find(method => method.id === cardId);
      if (card) {
        setFormCard({
          name: card.name,
          cardNumber: card.cardNumber,
          expiryMonth: card.expiryMonth,
          expiryYear: card.expiryYear,
          cvc: card.cvc || '',
          zip: card.zip || ''
        });
      }
    } else {
      // AUTO-FILL: Get user's name from auth context
      let autoFilledName = '';
      if (userData?.displayName) {
        autoFilledName = userData.displayName;
      } else if (userData?.firstName && userData?.lastName) {
        autoFilledName = `${userData.firstName} ${userData.lastName}`;
      } else if (userData?.firstName) {
        autoFilledName = userData.firstName;
      } else if (currentUser?.displayName) {
        autoFilledName = currentUser.displayName;
      }
      setSelectedCardId(null);
      setFormCard({
        name: autoFilledName,
        cardNumber: '',
        expiryMonth: '',
        expiryYear: '',
        cvc: '',
        zip: ''
      });
    }
    // Clear errors
    setCardErrors({
      name: '',
      cardNumber: '',
      expiry: '',
      cvc: '',
      zip: ''
    });
    setIsCardModalOpen(true);
  };
  const handleOpenRemoveCardModal = (cardId: string) => {
    setSelectedCardId(cardId);
    setIsRemoveCardModalOpen(true);
  };
  const handleRemoveCard = () => {
    setIsProcessing(true);
    setTimeout(() => {
      if (selectedCardId) {
        setPaymentMethods(paymentMethods.filter(method => method.id !== selectedCardId));
        showToast.success('Payment method removed.');
      }
      setIsRemoveCardModalOpen(false);
      setSelectedCardId(null);
      setIsProcessing(false);
    }, 500);
  };
  // Validate card form
  const validateCardForm = (): boolean => {
    const errors = {
      name: '',
      cardNumber: '',
      expiry: '',
      cvc: '',
      zip: ''
    };
    let isValid = true;
    if (!formCard.name.trim()) {
      errors.name = 'Name is required';
      isValid = false;
    }
    const cardNumber = formCard.cardNumber.replace(/\s/g, '');
    if (!cardNumber || cardNumber.length !== 16 || !/^\d+$/.test(cardNumber)) {
      errors.cardNumber = 'Enter a valid 16-digit card number';
      isValid = false;
    }
    const currentYear = new Date().getFullYear() % 100;
    const currentMonth = new Date().getMonth() + 1;
    if (!formCard.expiryMonth || !formCard.expiryYear) {
      errors.expiry = 'Expiry date is required';
      isValid = false;
    } else if (parseInt(formCard.expiryMonth) < 1 || parseInt(formCard.expiryMonth) > 12) {
      errors.expiry = 'Enter a valid month (01-12)';
      isValid = false;
    } else if (parseInt(formCard.expiryYear) < currentYear || parseInt(formCard.expiryYear) === currentYear && parseInt(formCard.expiryMonth) < currentMonth) {
      errors.expiry = 'Expiry date is in the past';
      isValid = false;
    }
    if (!formCard.cvc || !/^\d{3,4}$/.test(formCard.cvc)) {
      errors.cvc = 'Enter a valid 3 or 4 digit CVC';
      isValid = false;
    }
    if (!formCard.zip) {
      errors.zip = 'Billing ZIP/postal code is required';
      isValid = false;
    }
    setCardErrors(errors);
    return isValid;
  };
  const handleSaveCard = () => {
    if (!validateCardForm()) return;
    setIsProcessing(true);
    setTimeout(() => {
      const cardNumber = formCard.cardNumber.replace(/\s/g, '');
      const last4 = cardNumber.slice(-4);
      const brand = detectCardBrand(cardNumber);
      if (cardModalMode === 'edit' && selectedCardId) {
        setPaymentMethods(paymentMethods.map(method => method.id === selectedCardId ? {
          ...method,
          name: formCard.name,
          cardNumber,
          last4,
          expiryMonth: formCard.expiryMonth,
          expiryYear: formCard.expiryYear,
          cvc: formCard.cvc,
          zip: formCard.zip,
          brand
        } : method));
        showToast.success('Payment method updated.');
      } else {
        const newCard: PaymentMethod = {
          id: Date.now().toString(),
          type: 'card',
          name: formCard.name,
          cardNumber,
          last4,
          expiryMonth: formCard.expiryMonth,
          expiryYear: formCard.expiryYear,
          cvc: formCard.cvc,
          zip: formCard.zip,
          isDefault: paymentMethods.length === 0,
          brand
        };
        setPaymentMethods([...paymentMethods, newCard]);
        showToast.success('Payment method added.');
      }
      setIsCardModalOpen(false);
      setIsProcessing(false);
    }, 600);
  };
  // Format card number with spaces
  const formatCardNumber = (value: string): string => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    const matches = v.match(/\d{4,16}/g);
    const match = matches && matches[0] || '';
    const parts = [];
    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }
    if (parts.length) {
      return parts.join(' ');
    } else {
      return value;
    }
  };
  const handleCardNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCardNumber(e.target.value);
    setFormCard({
      ...formCard,
      cardNumber: formatted
    });
  };
  // Esc key handling
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (isCardModalOpen) {
          setIsCardModalOpen(false);
        }
        if (isRemoveCardModalOpen) {
          setIsRemoveCardModalOpen(false);
        }
        if (isManageSubOpen) {
          setIsManageSubOpen(false);
        }
      }
    };
    if (isCardModalOpen || isRemoveCardModalOpen || isManageSubOpen) {
      document.addEventListener('keydown', handleEscKey);
    }
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isCardModalOpen, isRemoveCardModalOpen, isManageSubOpen]);
  // Get status badge color
  const getStatusBadgeColor = () => {
    switch (subscription.status) {
      case 'active':
        return darkMode ? 'bg-green-900/30 text-green-300' : 'bg-green-100 text-green-800';
      case 'trial':
        return darkMode ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-100 text-blue-800';
      case 'canceled':
        return darkMode ? 'bg-red-900/30 text-red-300' : 'bg-red-100 text-red-800';
      default:
        return darkMode ? 'bg-gray-900/30 text-gray-300' : 'bg-gray-100 text-gray-800';
    }
  };
  return <div className="max-w-7xl mx-auto px-4" data-id="element-3086">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6" data-id="element-3087">
        <div className="mb-4 md:mb-0" data-id="element-3088">
          <h1 className="text-2xl sm:text-3xl font-bold" data-id="element-3089">Billing</h1>
        </div>
        <button onClick={handleOpenManageSub} disabled={isPortalLoading} className={`px-4 py-2 rounded-lg flex items-center justify-center border-2 ${darkMode ? 'border-[#10B981] text-[#10B981] hover:bg-[#10B981]/10' : 'border-[#10B981] text-[#10B981] hover:bg-[#10B981]/10'} transition-colors ${isPortalLoading ? 'opacity-60 cursor-not-allowed' : ''}`} data-id="element-3090">
          {isPortalLoading ? <>
              <LoaderIcon className="h-4 w-4 mr-2 animate-spin" data-id="element-3091" />
              Loading...
            </> : 'Manage Subscription'}
        </button>
      </div>

      {/* ⚠️ TEMPORARY TEST BUTTON — Remove after testing Paddle checkout */}
      <div className={`mb-4 p-4 rounded-xl border-2 border-dashed ${darkMode ? 'border-yellow-500/50 bg-yellow-900/20' : 'border-yellow-400 bg-yellow-50'}`} data-id="element-3092">
        <div className="flex items-center justify-between" data-id="element-3093">
          <div data-id="element-3094">
            <p className={`text-sm font-semibold ${darkMode ? 'text-yellow-300' : 'text-yellow-800'}`} data-id="element-3095">
              🧪 Paddle Checkout Test
            </p>
            <p className={`text-xs ${darkMode ? 'text-yellow-400/70' : 'text-yellow-600'}`} data-id="element-3096">
              Temporary button to test checkout flow. Remove after verifying.
            </p>
          </div>
          <button onClick={async () => {
          try {
            if (!currentUser) {
              showToast.error('You must be logged in to test checkout.');
              return;
            }
            await openPaddleCheckout(currentUser.email || '', currentUser.uid, currentUser.displayName || 'Test User');
          } catch (err: any) {
            console.error('[Paddle Test] Checkout error:', err);
            showToast.error(`Checkout failed: ${err.message}`);
          }
        }} className="px-4 py-2 rounded-lg bg-yellow-500 text-white font-semibold hover:bg-yellow-600 transition-colors text-sm" data-id="element-3097">
            Test Subscribe Now
          </button>
        </div>
      </div>

      {/* Two-Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-id="element-3098">
        {/* Left Column - Subscription & Transaction History (2/3 width) */}
        <div className="lg:col-span-2 space-y-4" data-id="element-3099">
          {/* Primary Subscription Card */}
          <div className={`${darkMode ? 'bg-navy-800/80' : 'bg-white'} rounded-xl shadow-sm p-4`} data-id="element-3100">
            <h2 className="text-base font-semibold mb-3" data-id="element-3101">Subscription</h2>

            <div className="flex items-start justify-between mb-3" data-id="element-3102">
              <div className="flex-1" data-id="element-3103">
                <div className="flex items-center gap-2 mb-1" data-id="element-3104">
                  <h3 className="text-lg font-semibold" data-id="element-3105">{subscription.plan}</h3>
                  <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${getStatusBadgeColor()}`} data-id="element-3106">
                    {subscription.status === 'active' ? 'Active' : subscription.status === 'trial' ? 'Trial' : 'Canceled'}
                  </span>
                </div>
                <div className="flex items-baseline gap-2 mb-2" data-id="element-3107">
                  <span className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-3108">
                    ${subscription.price.toFixed(2)}
                  </span>
                  <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-3109">
                    / month
                  </span>
                </div>
                <div className="flex items-center gap-1.5" data-id="element-3110">
                  <CalendarIcon className={`h-3.5 w-3.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-3111" />
                  <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-3112">
                    {subscription.status === 'canceled' ? `Access until ${subscription.nextBillingDate}` : `Next billing ${subscription.nextBillingDate}`}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Transaction History - Full Width Within Left Column */}
          <div className={`${darkMode ? 'bg-navy-800/80' : 'bg-white'} rounded-xl shadow-sm p-4`} data-id="element-3113">
            <div className="flex items-center mb-3" data-id="element-3114">
              <FileTextIcon className={`h-4 w-4 mr-2 ${darkMode ? 'text-slate-400' : 'text-slate-600'}`} aria-hidden="true" data-id="element-3115" />
              <h2 className="text-base font-semibold" data-id="element-3116">Transaction History</h2>
            </div>

            {/* Compact Empty State */}
            <div className={`p-6 rounded-lg ${darkMode ? 'bg-navy-900/50' : 'bg-gray-50'} text-center`} data-id="element-3117">
              <FileTextIcon className={`h-8 w-8 mx-auto mb-2 ${darkMode ? 'text-gray-600' : 'text-gray-400'}`} data-id="element-3118" />
              <p className={`text-sm mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-3119">
                No billing history yet. Subscription charges will appear here
                once billing is enabled.
              </p>
              <div className={`flex items-start justify-center gap-2 p-3 rounded-lg ${darkMode ? 'bg-navy-800/50' : 'bg-blue-50'}`} data-id="element-3120">
                <InfoIcon className={`h-4 w-4 mt-0.5 flex-shrink-0 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} data-id="element-3121" />
                <p className={`text-xs text-left ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-3122">
                  Billing is still being set up. Transaction history will appear
                  here once your subscription becomes active.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Compact Payment Method Card (1/3 width) */}
        <div className="lg:col-span-1" data-id="element-3123">
          <div className={`${darkMode ? 'bg-navy-800/80' : 'bg-white'} rounded-xl shadow-sm p-4`} data-id="element-3124">
            <h2 className="text-base font-semibold mb-3" data-id="element-3125">Payment Method</h2>

            {paymentMethods.length === 0 ? <div className={`p-4 rounded-lg ${darkMode ? 'bg-navy-900/50' : 'bg-gray-50'} text-center`} data-id="element-3126">
                <CreditCardIcon className={`h-6 w-6 mx-auto mb-2 ${darkMode ? 'text-gray-600' : 'text-gray-400'}`} data-id="element-3127" />
                <p className={`text-xs mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-3128">
                  No payment method added
                </p>
                <button onClick={() => handleOpenCardModal('add')} className={`inline-flex items-center justify-center gap-1.5 px-3 py-1.5 border-2 rounded-lg text-xs font-medium transition-colors ${darkMode ? 'border-[#10B981] text-[#10B981] hover:bg-[#10B981]/10' : 'border-[#10B981] text-[#10B981] hover:bg-[#10B981]/10'}`} data-id="element-3129">
                  Update Payment Method
                </button>
              </div> : <div className="space-y-3" data-id="element-3130">
                {paymentMethods.map(method => <div key={method.id} className={`p-3 rounded-lg ${darkMode ? 'bg-navy-900/50' : 'bg-gray-50'}`} data-id="element-3131">
                    <div className="flex items-start gap-2 mb-2" data-id="element-3132">
                      <CreditCardIcon className={`h-4 w-4 mt-0.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-3133" />
                      <div className="flex-1" data-id="element-3134">
                        <p className={`font-medium text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-3135">
                          {method.brand.charAt(0).toUpperCase() + method.brand.slice(1)}{' '}
                          ••{method.last4}
                        </p>
                        <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-3136">
                          Expires {method.expiryMonth}/{method.expiryYear}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2" data-id="element-3137">
                      <button onClick={() => handleOpenCardModal('edit', method.id)} className={`flex-1 px-2 py-1 rounded text-xs ${darkMode ? 'bg-navy-700 hover:bg-navy-600 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`} data-id="element-3138">
                        Edit
                      </button>
                      <button onClick={() => handleOpenRemoveCardModal(method.id)} className={`flex-1 px-2 py-1 rounded text-xs ${darkMode ? 'bg-red-900/30 hover:bg-red-900/50 text-red-300' : 'bg-red-50 hover:bg-red-100 text-red-700'}`} data-id="element-3139">
                        Remove
                      </button>
                    </div>
                  </div>)}
                <button onClick={() => handleOpenCardModal('add')} className={`flex items-center justify-center w-full px-2 py-1.5 rounded-lg text-xs ${darkMode ? 'text-[#10B981] hover:bg-[#10B981]/10' : 'text-[#10B981] hover:bg-[#10B981]/10'} transition-colors`} data-id="element-3140">
                  Update Payment Method
                </button>
              </div>}
          </div>
        </div>
      </div>

      {/* All modals remain unchanged */}
      {/* Manage Subscription Modal */}
      {isManageSubOpen && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={e => {
      if (e.target === e.currentTarget) {
        setIsManageSubOpen(false);
      }
    }} data-id="element-3141">
          <div className={`${darkMode ? 'bg-navy-800' : 'bg-white'} rounded-xl p-6 max-w-md w-full mx-4`} data-id="element-3142">
            <div className="flex justify-between items-center mb-4" data-id="element-3143">
              <h3 className={`text-xl font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-3144">
                Manage Subscription
              </h3>
              <button onClick={() => setIsManageSubOpen(false)} className={`p-1 rounded-full ${darkMode ? 'hover:bg-navy-700' : 'hover:bg-gray-200'}`} data-id="element-3145">
                <XIcon className={`h-6 w-6 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-3146" />
              </button>
            </div>
            <div className={`p-4 rounded-lg ${darkMode ? 'bg-navy-700' : 'bg-gray-50'} mb-6`} data-id="element-3147">
              <div className="flex items-center justify-between mb-2" data-id="element-3148">
                <h4 className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-3149">
                  Current Plan
                </h4>
                <span className={`px-2 py-0.5 text-xs rounded-full ${getStatusBadgeColor()}`} data-id="element-3150">
                  {subscription.status === 'active' ? 'Active' : subscription.status === 'trial' ? 'Trial' : 'Canceled'}
                </span>
              </div>
              <div className="flex items-baseline mb-1" data-id="element-3151">
                <span className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-3152">
                  {subscription.plan}
                </span>
              </div>
              <p className={`text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-3153">
                ${subscription.price.toFixed(2)} / month
              </p>
              <div className="flex items-center mt-4" data-id="element-3154">
                <CalendarIcon className={`h-5 w-5 ${darkMode ? 'text-gray-400' : 'text-gray-500'} mr-2`} data-id="element-3155" />
                <p className={`${darkMode ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-3156">
                  {subscription.status === 'canceled' ? `Access until: ${subscription.nextBillingDate}` : `Next billing: ${subscription.nextBillingDate}`}
                </p>
              </div>
            </div>
            <div className="flex justify-end" data-id="element-3157">
              {subscription.status === 'active' || subscription.status === 'trial' ? <button onClick={handleOpenConfirmCancel} className={`px-4 py-2 ${darkMode ? 'bg-red-900/30 hover:bg-red-900/50 text-red-300' : 'bg-red-50 hover:bg-red-100 text-red-700'} rounded-lg transition-colors`} data-id="element-3158">
                  Cancel subscription
                </button> : <button onClick={() => setIsManageSubOpen(false)} className={`px-4 py-2 ${darkMode ? 'bg-navy-700 hover:bg-navy-600' : 'bg-gray-100 hover:bg-gray-200'} rounded-lg transition-colors`} data-id="element-3159">
                  Close
                </button>}
            </div>
          </div>
        </div>}

      {/* Confirm Cancellation Modal */}
      {isConfirmCancelOpen && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-id="element-3160">
          <div className={`${darkMode ? 'bg-navy-800' : 'bg-white'} rounded-xl p-6 max-w-md w-full mx-4`} data-id="element-3161">
            <div className="flex items-center mb-4" data-id="element-3162">
              <AlertCircleIcon className="h-6 w-6 text-red-500 mr-2" data-id="element-3163" />
              <h3 className={`text-xl font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-3164">
                Cancel subscription?
              </h3>
            </div>
            <p className={`mb-6 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-3165">
              You'll lose premium features at the end of your current billing
              period. You can re-subscribe anytime.
            </p>
            <div className="flex justify-end space-x-3" data-id="element-3166">
              <button onClick={() => setIsConfirmCancelOpen(false)} className={`px-4 py-2 ${darkMode ? 'border border-gray-500 text-gray-300 hover:bg-navy-700' : 'border border-gray-300 text-gray-700 hover:bg-gray-100'} rounded-lg transition-colors`} data-id="element-3167">
                Back
              </button>
              <button onClick={handleConfirmCancel} disabled={isProcessing} className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors" data-id="element-3168">
                {isProcessing ? 'Processing...' : 'Confirm Cancel'}
              </button>
            </div>
          </div>
        </div>}

      {/* Card Modal (Edit/Add) */}
      {isCardModalOpen && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={e => {
      if (e.target === e.currentTarget) {
        setIsCardModalOpen(false);
      }
    }} data-id="element-3169">
          <div className={`${darkMode ? 'bg-navy-800' : 'bg-white'} rounded-xl p-6 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto`} data-id="element-3170">
            <div className="flex justify-between items-center mb-4" data-id="element-3171">
              <h3 className={`text-xl font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-3172">
                {cardModalMode === 'add' ? 'Add Payment Method' : 'Edit Payment Method'}
              </h3>
              <button onClick={() => setIsCardModalOpen(false)} className={`p-1 rounded-full ${darkMode ? 'hover:bg-navy-700' : 'hover:bg-gray-200'}`} data-id="element-3173">
                <XIcon className={`h-6 w-6 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-3174" />
              </button>
            </div>
            <div className="space-y-4" data-id="element-3175">
              <div data-id="element-3176">
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-3177">
                  Cardholder Name
                </label>
                <input type="text" value={formCard.name} onChange={e => setFormCard({
              ...formCard,
              name: e.target.value
            })} className={`w-full px-4 py-2.5 rounded-lg border ${darkMode ? 'bg-navy-900/50 border-navy-700/50 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'} ${cardErrors.name ? 'border-red-500 focus:border-red-500' : darkMode ? 'focus:border-[#10B981] focus:ring-1 focus:ring-[#10B981]/20' : 'focus:border-deep-pink focus:ring-1 focus:ring-deep-pink/20'} shadow-sm transition-all`} placeholder="John Smith" data-id="element-3178" />
                {cardErrors.name && <p className="mt-1.5 text-sm text-red-400" data-id="element-3179">
                    {cardErrors.name}
                  </p>}
              </div>
              <div data-id="element-3180">
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-3181">
                  Card Number
                </label>
                <div className="relative" data-id="element-3182">
                  <input type="text" value={formCard.cardNumber} onChange={handleCardNumberChange} className={`w-full px-4 py-2.5 rounded-lg border ${darkMode ? 'bg-navy-900/50 border-navy-700/50 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'} ${cardErrors.cardNumber ? 'border-red-500 focus:border-red-500' : darkMode ? 'focus:border-[#10B981] focus:ring-1 focus:ring-[#10B981]/20' : 'focus:border-deep-pink focus:ring-1 focus:ring-deep-pink/20'} shadow-sm transition-all`} placeholder="4242 4242 4242 4242" maxLength={19} data-id="element-3183" />
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2" data-id="element-3184">
                    <CreditCardIcon className={`h-5 w-5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-3185" />
                  </div>
                </div>
                {cardErrors.cardNumber && <p className="mt-1.5 text-sm text-red-400" data-id="element-3186">
                    {cardErrors.cardNumber}
                  </p>}
              </div>
              <div className="grid grid-cols-2 gap-4" data-id="element-3187">
                <div data-id="element-3188">
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-3189">
                    Expiration (MM/YY)
                  </label>
                  <div className="flex space-x-2" data-id="element-3190">
                    <select value={formCard.expiryMonth} onChange={e => setFormCard({
                  ...formCard,
                  expiryMonth: e.target.value
                })} className={`w-1/2 px-3 py-2.5 rounded-lg border ${darkMode ? 'bg-navy-900/50 border-navy-700/50 text-white' : 'bg-white border-gray-300 text-gray-900'} ${cardErrors.expiry ? 'border-red-500 focus:border-red-500' : darkMode ? 'focus:border-[#10B981] focus:ring-1 focus:ring-[#10B981]/20' : 'focus:border-deep-pink focus:ring-1 focus:ring-deep-pink/20'} shadow-sm transition-all`} data-id="element-3191">
                      <option value="" data-id="element-3192">MM</option>
                      {Array.from({
                    length: 12
                  }, (_, i) => {
                    const month = i + 1;
                    return <option key={month} value={month.toString().padStart(2, '0')} data-id="element-3193">
                              {month.toString().padStart(2, '0')}
                            </option>;
                  })}
                    </select>
                    <select value={formCard.expiryYear} onChange={e => setFormCard({
                  ...formCard,
                  expiryYear: e.target.value
                })} className={`w-1/2 px-3 py-2.5 rounded-lg border ${darkMode ? 'bg-navy-900/50 border-navy-700/50 text-white' : 'bg-white border-gray-300 text-gray-900'} ${cardErrors.expiry ? 'border-red-500 focus:border-red-500' : darkMode ? 'focus:border-[#10B981] focus:ring-1 focus:ring-[#10B981]/20' : 'focus:border-deep-pink focus:ring-1 focus:ring-deep-pink/20'} shadow-sm transition-all`} data-id="element-3194">
                      <option value="" data-id="element-3195">YY</option>
                      {Array.from({
                    length: 10
                  }, (_, i) => {
                    const year = new Date().getFullYear() + i;
                    return <option key={year} value={year.toString().slice(-2)} data-id="element-3196">
                              {year.toString().slice(-2)}
                            </option>;
                  })}
                    </select>
                  </div>
                  {cardErrors.expiry && <p className="mt-1.5 text-sm text-red-400" data-id="element-3197">
                      {cardErrors.expiry}
                    </p>}
                </div>
                <div data-id="element-3198">
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-3199">
                    CVC
                  </label>
                  <input type="text" value={formCard.cvc} onChange={e => setFormCard({
                ...formCard,
                cvc: e.target.value
              })} className={`w-full px-3 py-2.5 rounded-lg border ${darkMode ? 'bg-navy-900/50 border-navy-700/50 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'} ${cardErrors.cvc ? 'border-red-500 focus:border-red-500' : darkMode ? 'focus:border-[#10B981] focus:ring-1 focus:ring-[#10B981]/20' : 'focus:border-deep-pink focus:ring-1 focus:ring-deep-pink/20'} shadow-sm transition-all`} placeholder="123" maxLength={4} data-id="element-3200" />
                  {cardErrors.cvc && <p className="mt-1.5 text-sm text-red-400" data-id="element-3201">
                      {cardErrors.cvc}
                    </p>}
                </div>
              </div>
              <div data-id="element-3202">
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-3203">
                  Billing ZIP/Postal Code
                </label>
                <input type="text" value={formCard.zip} onChange={e => setFormCard({
              ...formCard,
              zip: e.target.value
            })} className={`w-full px-4 py-2.5 rounded-lg border ${darkMode ? 'bg-navy-900/50 border-navy-700/50 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'} ${cardErrors.zip ? 'border-red-500 focus:border-red-500' : darkMode ? 'focus:border-[#10B981] focus:ring-1 focus:ring-[#10B981]/20' : 'focus:border-deep-pink focus:ring-1 focus:ring-deep-pink/20'} shadow-sm transition-all`} placeholder="10001" data-id="element-3204" />
                {cardErrors.zip && <p className="mt-1.5 text-sm text-red-400" data-id="element-3205">
                    {cardErrors.zip}
                  </p>}
              </div>
            </div>
            <div className="mt-6 flex justify-end space-x-3" data-id="element-3206">
              <button onClick={() => setIsCardModalOpen(false)} className={`px-4 py-2 rounded-lg text-sm font-medium ${darkMode ? 'bg-navy-700/50 text-white hover:bg-navy-700' : 'border border-gray-300 text-gray-700 hover:bg-gray-100'} transition-colors`} data-id="element-3207">
                Cancel
              </button>
              <button onClick={handleSaveCard} disabled={isProcessing} className={`px-4 py-2 border-2 rounded-lg text-sm font-medium ${darkMode ? 'border-[#10B981] text-[#10B981] hover:bg-[#10B981]/10' : 'border-[#10B981] text-[#10B981] hover:bg-[#10B981]/10'} disabled:border-gray-600 disabled:text-gray-600 disabled:cursor-not-allowed transition-colors`} data-id="element-3208">
                {isProcessing ? 'Processing...' : 'Save Payment Method'}
              </button>
            </div>
          </div>
        </div>}

      {/* Remove Card Confirmation Modal */}
      {isRemoveCardModalOpen && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={e => {
      if (e.target === e.currentTarget) {
        setIsRemoveCardModalOpen(false);
      }
    }} data-id="element-3209">
          <div className={`${darkMode ? 'bg-navy-800' : 'bg-white'} rounded-xl p-6 max-w-md w-full mx-4`} data-id="element-3210">
            <div className="flex items-center mb-4" data-id="element-3211">
              <AlertCircleIcon className="h-6 w-6 text-red-500 mr-2" data-id="element-3212" />
              <h3 className={`text-xl font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-3213">
                Remove payment method?
              </h3>
            </div>
            <p className={`mb-6 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-3214">
              Are you sure you want to remove this payment method? You can add
              it again later.
            </p>
            <div className="flex justify-end space-x-3" data-id="element-3215">
              <button onClick={() => setIsRemoveCardModalOpen(false)} className={`px-4 py-2 ${darkMode ? 'border border-gray-500 text-gray-300 hover:bg-navy-700' : 'border border-gray-300 text-gray-700 hover:bg-gray-100'} rounded-lg transition-colors`} data-id="element-3216">
                Keep
              </button>
              <button onClick={handleRemoveCard} disabled={isProcessing} className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors" data-id="element-3217">
                {isProcessing ? 'Processing...' : 'Remove'}
              </button>
            </div>
          </div>
        </div>}
    </div>;
};